import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Plus, ClipboardList, User, Calendar, Flag, Filter, X, Save, Eraser, FileSpreadsheet, LayoutList, LayoutGrid } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import demandasService from '../services/demandasService';
import DemandasGrid from '../components/DemandasGrid';
import usersService from '../services/usersService';
import funcionariosService from '../services/funcionariosService';
import IconButton from '../components/IconButton';

const STATUS_COLUMNS = ['Backlog', 'Planejamento', 'Em andamento', 'Concluído'];
const PRIORIDADES = ['Baixa', 'Normal', 'Alta', 'Urgente'];

const Demandas = () => {
  const { isAuthenticated, user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smartQuery, setSmartQuery] = useState('');
  const [statusMulti, setStatusMulti] = useState([]);
  // Suporte a múltiplos filtros de data simultâneos (AND)
  const [dateFilters, setDateFilters] = useState({
    dt_cad_dem: { start: '', end: '' },
    dt_cad_ini: { start: '', end: '' },
    dt_prev_dem: { start: '', end: '' },
    dt_prev_dem_seg: { start: '', end: '' },
    dt_concl_dem: { start: '', end: '' },
  });
  // Popover de filtros de data abaixo da barra
  const [showDatePopover, setShowDatePopover] = useState(false);
  const [draftDateFilters, setDraftDateFilters] = useState({
    dt_cad_dem: { start: '', end: '' },
    dt_cad_ini: { start: '', end: '' },
    dt_prev_dem: { start: '', end: '' },
    dt_prev_dem_seg: { start: '', end: '' },
    dt_concl_dem: { start: '', end: '' },
  });
  const openDatePopover = () => {
    setDraftDateFilters(dateFilters);
    setShowDatePopover(true);
  };
  const applyDatePopover = () => {
    setDateFilters(draftDateFilters);
    setShowDatePopover(false);
  };
  const clearDatePopover = () => {
    const cleared = {
      dt_cad_dem: { start: '', end: '' },
      dt_cad_ini: { start: '', end: '' },
      dt_prev_dem: { start: '', end: '' },
      dt_prev_dem_seg: { start: '', end: '' },
      dt_concl_dem: { start: '', end: '' },
    };
    // Zera rascunho e filtros ativos para remover imediatamente os filtros
    setDraftDateFilters(cleared);
    setDateFilters(cleared);
  };
  const [form, setForm] = useState({ descr_dem: '', respon_dem: user?.id || '', solic_dem: user?.id || '', pri_dem: 'Normal', dt_prev_dem: '', dt_prev_dem_seg: '' });
  const [creating, setCreating] = useState(false);
  const [draggingItem, setDraggingItem] = useState(null);
  const [viewMode, setViewMode] = useState('board'); // 'board' | 'grid'
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);

  const load = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const { data } = await demandasService.list();
      setItems(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar demandas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAuthenticated]);

  const loadUsers = async () => {
    if (!isAuthenticated) return;
    try {
      const resp = user?.role === 'admin'
        ? await usersService.list({ page: 1, limit: 1000 })
        : await usersService.listBasic({ page: 1, limit: 1000 });
      const data = resp?.data || {};
      const list = Array.isArray(data?.users) ? data.users : [];
      setUsers(list);
      // se não houver valor no form, setar padrão para usuário logado
      setForm(f => ({ 
        ...f, 
        respon_dem: f.respon_dem || user?.id || '',
        solic_dem: f.solic_dem || user?.id || ''
      }));
    } catch (err) {
      console.warn('Falha ao carregar usuários, usando fallback do usuário atual. Erro:', err?.response?.status || err);
      if (user) {
        setUsers([{ id: user.id, name: user.name }]);
        setForm(f => ({ ...f, respon_dem: user.id, solic_dem: user.id }));
      }
    }
  };

  const loadFuncionarios = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await funcionariosService.listar({ page: 1, limit: 1000 });
      const list = Array.isArray(res?.data) ? res.data : [];
      setFuncionarios(list);
    } catch (err) {
      console.warn('Falha ao carregar funcionários. Erro:', err?.response?.status || err);
    }
  };

  useEffect(() => { loadUsers(); loadFuncionarios(); }, [isAuthenticated]);

  // Utilidades de normalização para busca inteligente
  const normalizeText = (v) => {
    if (v == null) return '';
    try {
      return String(v)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    } catch (_) {
      return String(v).toLowerCase().trim();
    }
  };

  const getPersonName = (idOrName) => {
    if (!idOrName) return '—';
    const u = users.find(u => String(u.id) === String(idOrName));
    if (u) return u.name;
    const f = funcionarios.find(f => String(f.id_func) === String(idOrName));
    return f ? f.nome_func : String(idOrName);
  };

  // Parser simples: tokens "chave:valor" + termos livres
  const parseSmartQuery = (q) => {
    const tokens = [];
    const free = [];
    const raw = normalizeText(q);
    if (!raw) return { tokens, free };
    raw.split(/\s+/).forEach(part => {
      const [k, ...rest] = part.split(':');
      if (rest.length > 0) {
        const v = rest.join(':');
        tokens.push({ key: k, value: v });
      } else {
        free.push(part);
      }
    });
    return { tokens, free };
  };

  // Mapeamento de chaves aceitas e sinônimos
  const KEY_MAP = {
    // Mapeamentos principais + atalhos simples
    status: ['status', 'st', 's'],
    responsavel: ['responsavel', 'responsável', 'resp', 'r'],
    descricao: ['descricao', 'descrição', 'obs', 'observacao', 'observação', 'd'],
    solicitante: ['solicitante', 'sol', 'solicit'],
    prioridade: ['prioridade', 'pri', 'p'],
    id: ['id', '#'],
    dt_prev_dem: ['prev', 'dataprevista', 'dtprev'],
    dt_cad_ini: ['inicio', 'dtini'],
    hora_ini_dem: ['horaini', 'hini'],
    hora_fin_dem: ['horafim', 'hfim'],
    dt_prev_dem_seg: ['reagendada', 'dtseg'],
    dt_concl_dem: ['concl', 'concluido']
  };

  const keyMatch = (key) => {
    const nk = normalizeText(key);
    for (const field in KEY_MAP) {
      if (KEY_MAP[field].includes(nk)) return field;
    }
    return null;
  };

  const dateToKey = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth()+1).padStart(2,'0');
      const da = String(dt.getDate()).padStart(2,'0');
      return `${y}-${m}-${da}`;
    } catch (_) {
      return String(d);
    }
  };

  // Helpers para exibição e parsing de datas no formato BR (dd/mm/aaaa)
  const toBR = (iso) => {
    if (!iso) return '';
    try {
      const s = String(iso).substring(0,10);
      const [y, m, d] = s.split('-');
      if (!y || !m || !d) return '';
      return `${d}/${m}/${y}`;
    } catch (_) {
      return '';
    }
  };

  const fromBRToISO = (br) => {
    if (!br) return '';
    const m = String(br).match(/^\d{2}\/\d{2}\/\d{4}$/);
    if (!m) return '';
    const [d, mth, y] = br.split('/');
    return `${y}-${mth}-${d}`;
  };

  const normalizeDateInput = (v) => {
    if (!v) return '';
    const s = String(v).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const iso = fromBRToISO(s);
    return iso || '';
  };

  // Componente: input de texto dd/mm/aaaa com ícone que abre o picker nativo
  const DateInputWithIcon = ({ value, onChange }) => {
    const ref = useRef(null);
    const openPicker = () => {
      try {
        if (ref.current?.showPicker) ref.current.showPicker();
        else if (ref.current) ref.current.click();
      } catch (_) {
        if (ref.current) ref.current.click();
      }
    };
    return (
      <div className="relative mt-1">
        <input
          type="text"
          placeholder="dd/mm/aaaa"
          value={toBR(value)}
          onChange={(e) => onChange(normalizeDateInput(e.target.value))}
          className="w-full h-9 text-sm px-2 pr-8 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <Calendar
          className="absolute right-2 top-2 w-4 h-4 text-gray-400 cursor-pointer"
          onClick={openPicker}
        />
        <input
          ref={ref}
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="absolute top-0 left-0 w-0 h-0 opacity-0 pointer-events-none"
        />
      </div>
    );
  };

  const inRange = (value, start, end) => {
    if (!start && !end) return true;
    const key = dateToKey(value);
    if (!key) return false;
    if (start && key < start) return false;
    if (end && key > end) return false;
    return true;
  };

  const inAllDateRanges = (item) => {
    const anyActive = Object.values(dateFilters).some(f => f.start || f.end);
    if (!anyActive) return true;
    const checks = [
      ['dt_cad_dem', dateFilters.dt_cad_dem],
      ['dt_cad_ini', dateFilters.dt_cad_ini],
      ['dt_prev_dem', dateFilters.dt_prev_dem],
      ['dt_prev_dem_seg', dateFilters.dt_prev_dem_seg],
      ['dt_concl_dem', dateFilters.dt_concl_dem],
    ];
    return checks.every(([field, range]) => inRange(item[field], range.start, range.end));
  };

  // Aplica filtro inteligente sobre todos os campos
  const displayItems = useMemo(() => {
    const { tokens, free } = parseSmartQuery(smartQuery);
    // Se não houver consulta, nem status múltiplos, nem datas ativas, retorna itens
    const anyDateActive = Object.values(dateFilters).some(f => f.start || f.end);
    if (tokens.length === 0 && free.length === 0 && (!statusMulti || statusMulti.length === 0) && !anyDateActive) return items;

    const freeTerm = free.join(' ');
    const nFree = normalizeText(freeTerm);

    const matchesFree = (item) => {
      if (!nFree) return true;
      const campos = [
        item.descr_dem,
        item.obs_dem,
        item.status,
        item.pri_dem,
        getPersonName(item.respon_dem),
        getPersonName(item.solic_dem),
        item.id_dem,
        dateToKey(item.dt_prev_dem),
        dateToKey(item.dt_cad_ini),
        dateToKey(item.dt_concl_dem)
      ].map(normalizeText);
      return campos.some(c => c.includes(nFree));
    };

    const matchesToken = (item, t) => {
      const field = keyMatch(t.key);
      if (!field) {
        // chave desconhecida: tenta em texto livre
        return matchesFree(item);
      }
      const nv = normalizeText(t.value);
      if (field === 'responsavel') {
        // Nome resolvido (usuário ou funcionário), ou ID/valor bruto como string
        const name = normalizeText(getPersonName(item.respon_dem));
        if (name.includes(nv)) return true;
        const raw = normalizeText(item.respon_dem);
        if (raw.includes(nv)) return true;
        const candidateIds = [
          ...users.filter(u => normalizeText(u.name).includes(nv)).map(u => String(u.id)),
          ...funcionarios.filter(f => normalizeText(f.nome_func).includes(nv)).map(f => String(f.id_func))
        ];
        return candidateIds.includes(String(item.respon_dem));
      }
      if (field === 'solicitante') {
        const name = normalizeText(getPersonName(item.solic_dem));
        return name.includes(nv) || normalizeText(item.solic_dem).includes(nv);
      }
      if (field === 'descricao') {
        // Busca em descrição e observação
        const d1 = normalizeText(item.descr_dem);
        const d2 = normalizeText(item.obs_dem);
        return d1.includes(nv) || d2.includes(nv);
      }
      if (field === 'id') {
        return normalizeText(item.id_dem).includes(nv);
      }
      // datas/horas: normaliza para yyyy-mm-dd ou HH:mm
      const val = item[field];
      if (field.startsWith('dt_')) {
        return normalizeText(dateToKey(val)).includes(nv);
      }
      return normalizeText(val).includes(nv);
    };

    return items.filter(item => {
      // todos os tokens devem bater
      const tokensOk = tokens.every(t => matchesToken(item, t));
      const freeOk = matchesFree(item);
      const statusOk = !statusMulti || statusMulti.length === 0 ? true : statusMulti.includes(item.status || 'Backlog');
      const dateOk = inAllDateRanges(item);
      return tokensOk && freeOk && statusOk && dateOk;
    });
  }, [items, smartQuery, users, funcionarios, statusMulti, dateFilters]);

  const grouped = useMemo(() => {
    const g = STATUS_COLUMNS.reduce((acc, s) => { acc[s] = []; return acc; }, {});
    displayItems.forEach(i => { g[i.status || 'Backlog'].push(i); });
    return g;
  }, [displayItems]);

  // getUserName movido acima para uso pelo filtro inteligente

  const onCreate = async () => {
    if (!form.descr_dem?.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    setCreating(true);
    try {
      // Normaliza payload: strings vazias -> null; horas HH:mm -> HH:mm:00
      const normalizeCreatePayload = (p) => {
        const out = { ...p };
        const dateFields = ['dt_cad_ini', 'dt_prev_dem', 'dt_prev_dem_seg', 'dt_concl_dem', 'dt_cad_dem'];
        const timeFields = ['hora_ini_dem', 'hora_fin_dem'];
        dateFields.forEach((k) => {
          if (k in out) {
            if (!out[k]) out[k] = null;
          }
        });
        timeFields.forEach((k) => {
          if (k in out) {
            const v = out[k];
            if (!v) {
              out[k] = null;
            } else {
              const m = String(v).match(/^\d{1,2}:\d{2}$/);
              if (m) out[k] = `${v}:00`;
            }
          }
        });
        return out;
      };

      const base = { ...form, status: 'Backlog', dt_cad_dem: new Date() };
      const payload = normalizeCreatePayload(base);
      await demandasService.create(payload);
      setForm({ descr_dem: '', respon_dem: user?.id || '', solic_dem: user?.id || '', pri_dem: 'Normal', dt_prev_dem: '', dt_prev_dem_seg: '' });
      setShowModal(false);
      toast.success('Demanda criada');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao criar demanda');
    } finally {
      setCreating(false);
    }
  };

  const openModal = () => {
    // Inicializa o formulário com defaults atuais
    setForm(f => ({
      descr_dem: '',
      respon_dem: user?.id || '',
      solic_dem: user?.id || '',
      pri_dem: 'Normal',
      dt_prev_dem: '',
      dt_prev_dem_seg: ''
    }));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const changeStatus = async (item, status) => {
    try {
      await demandasService.updateStatus(item.id_dem, status);
      toast.success('Status atualizado');
      load();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar status');
    }
  };

  const updateQuick = async (item, patch) => {
    // Normaliza payloads para MSSQL: datas vazias -> null; horas HH:mm -> HH:mm:00
    const normalizePatch = (p) => {
      const out = { ...p };
      // Se status for Concluído, inserir data atual em dt_concl_dem (se não existir)
      if ('status' in out && String(out.status) === 'Concluído') {
        if (!out.dt_concl_dem && !item.dt_concl_dem) {
          out.dt_concl_dem = new Date();
        }
      }
      const dateFields = ['dt_cad_ini', 'dt_prev_dem', 'dt_prev_dem_seg', 'dt_concl_dem', 'dt_cad_dem'];
      const timeFields = ['hora_ini_dem', 'hora_fin_dem'];
      dateFields.forEach((k) => {
        if (k in out) {
          if (!out[k]) {
            out[k] = null;
          }
        }
      });
      timeFields.forEach((k) => {
        if (k in out) {
          const v = out[k];
          if (!v) {
            out[k] = null;
          } else {
            const m = String(v).match(/^\d{1,2}:\d{2}$/);
            if (m) out[k] = `${v}:00`;
          }
        }
      });
      return out;
    };

    try {
      const payload = normalizePatch(patch);
      await demandasService.update(item.id_dem, payload);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar demanda \n Erro interno do servidor. Tente novamente mais tarde.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg"><ClipboardList className="h-6 w-6 text-blue-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Demandas</h1>
            <p className="text-gray-600">Gestão de tarefas por Quadro e Grid</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-gray-100 rounded-md p-1 flex items-center gap-1">
            <IconButton
              icon={LayoutList}
              onClick={() => setViewMode('board')}
              variant={viewMode === 'board' ? 'primary' : 'secondary'}
              size="sm"
              title="Quadro"
            />
            <IconButton
              icon={LayoutGrid}
              onClick={() => setViewMode('grid')}
              variant={viewMode === 'grid' ? 'primary' : 'secondary'}
              size="sm"
              title="Grid"
            />
          </div>
          {/* Filtro inteligente movido abaixo dos Filtros de Data */}
          <IconButton
            icon={FileSpreadsheet}
            onClick={() => {
              const headers = ['ID','Título','Responsável','Solicitante','Data Cadastro','Data Início','Hora Início','Hora Final','Data Prevista','Data Reagendada','Data Conclusão','Status','Prioridade'];
              const rows = displayItems.map(i => [
                i.id_dem,
                (i.descr_dem||'').replace(/\n/g,' '),
                getUserName(i.respon_dem)||'',
                getUserName(i.solic_dem)||'',
                i.dt_cad_dem||'',
                i.dt_cad_ini||'',
                i.hora_ini_dem||'',
                i.hora_fin_dem||'',
                i.dt_prev_dem||'',
                i.dt_prev_dem_seg||'',
                i.dt_concl_dem||'',
                i.status||'',
                i.pri_dem||''
              ]);
              const csv = [headers, ...rows]
                .map(r => r.map(v => `"${String(v??'').replace(/"/g,'""')}"`).join(','))
                .join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `demandas_${new Date().toISOString().substring(0,10)}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            variant="success"
            title="Exportar CSV"
          />
      </div>
    </div>

      {/* Linha abaixo com Filtros de Data ao modelo do Relatório */}
      <div className="relative mt-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Filtros de Data</span>
          <div className="relative inline-block">
            <IconButton
              icon={Filter}
              onClick={openDatePopover}
              variant="primary"
              title="Configurar filtros de data"
            />
            {showDatePopover && (
              <div className="absolute left-0 top-full z-40 mt-2 w-[520px] bg-white rounded shadow-lg border">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <div className="flex items-center gap-2 text-gray-800">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium text-sm">Datas</span>
                  </div>
                  <IconButton
                    icon={X}
                    onClick={() => setShowDatePopover(false)}
                    variant="secondary"
                    size="sm"
                    title="Fechar"
                  />
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Cadastro — Início</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_cad_dem.start}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_cad_dem: { ...prev.dt_cad_dem, start: v } }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Cadastro — Fim</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_cad_dem.end}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_cad_dem: { ...prev.dt_cad_dem, end: v } }))}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">Início — Início</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_cad_ini.start}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_cad_ini: { ...prev.dt_cad_ini, start: v } }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Início — Fim</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_cad_ini.end}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_cad_ini: { ...prev.dt_cad_ini, end: v } }))}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">Prevista — Início</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_prev_dem.start}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_prev_dem: { ...prev.dt_prev_dem, start: v } }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Prevista — Fim</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_prev_dem.end}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_prev_dem: { ...prev.dt_prev_dem, end: v } }))}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">Reagendada — Início</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_prev_dem_seg.start}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_prev_dem_seg: { ...prev.dt_prev_dem_seg, start: v } }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Reagendada — Fim</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_prev_dem_seg.end}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_prev_dem_seg: { ...prev.dt_prev_dem_seg, end: v } }))}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-600">Conclusão — Início</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_concl_dem.start}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_concl_dem: { ...prev.dt_concl_dem, start: v } }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Conclusão — Fim</label>
                      <DateInputWithIcon
                        value={draftDateFilters.dt_concl_dem.end}
                        onChange={(v) => setDraftDateFilters(prev => ({ ...prev, dt_concl_dem: { ...prev.dt_concl_dem, end: v } }))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <IconButton
                      icon={Eraser}
                      onClick={clearDatePopover}
                      variant="secondary"
                      size="sm"
                      title="Limpar"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                  <IconButton
                    icon={Eraser}
                    onClick={() => { setShowDatePopover(false); clearDatePopover(); }}
                    variant="secondary"
                    title="Limpar"
                  />
                  <IconButton
                    icon={Filter}
                    onClick={applyDatePopover}
                    variant="primary"
                    title="Aplicar filtro"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtro inteligente abaixo dos Filtros de Data */}
      <div className="mt-2">
        <label className="text-sm text-gray-700">Filtro inteligente</label>
        <div className="mt-1">
          <input
            type="text"
            placeholder="R:joao S:andamento D:cartão"
            value={smartQuery}
            onChange={(e) => setSmartQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const currentValue = e.currentTarget.value.trim();
                setSmartQuery(currentValue);
                e.currentTarget.blur();
              }
            }}
            className="h-9 w-full max-w-lg px-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Aliases: R (responsável), S (status), D (descrição e observação), P (prioridade). Ex.: R:joao S:andamento D:cartão P:Alta
          </p>
        </div>
      </div>

      {/* Ações de criação */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center">
          <IconButton
            icon={Plus}
            onClick={openModal}
            variant="primary"
            title="Adicionar Demandas"
          />
        </div>
      </div>

      {/* Alternância entre Quadro e Grid */}
      {viewMode === 'board' ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {STATUS_COLUMNS.map((col) => (
            <div key={col} className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700">{col}</span>
                <span className="text-xs text-gray-500">{grouped[col].length} itens</span>
              </div>
              <div
                className="space-y-2 min-h-24"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (draggingItem) {
                    changeStatus(draggingItem, col);
                    setDraggingItem(null);
                  }
                }}
              >
                {loading && grouped[col].length === 0 && (
                  <div className="text-xs text-gray-500">Carregando...</div>
                )}
                {grouped[col].map(item => (
                  <div
                    key={item.id_dem}
                    className={`border border-gray-200 rounded-md p-3 bg-white ${draggingItem?.id_dem === item.id_dem ? 'opacity-60' : ''}`}
                    draggable
                    onDragStart={() => setDraggingItem(item)}
                    onDragEnd={() => setDraggingItem(null)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="text-sm font-medium text-gray-900">{item.descr_dem}</div>
                      <select
                        value={item.status || 'Backlog'}
                        onChange={(e) => changeStatus(item, e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded"
                      >
                        {STATUS_COLUMNS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-700">
                    <div className="flex items-center gap-1"><User className="h-3 w-3" />{getPersonName(item.respon_dem)}</div>
                      <div className="flex items-center gap-1"><Flag className="h-3 w-3" />{item.pri_dem || 'Normal'}</div>
                      <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{toBR(item.dt_prev_dem)}</div>
                    </div>
                    <div className="mt-2">
                      <textarea
                        rows={2}
                        placeholder="Observações"
                        defaultValue={item.obs_dem || ''}
                        onBlur={(e) => updateQuick(item, { obs_dem: e.target.value })}
                        className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <DemandasGrid
          items={displayItems}
          loading={loading}
          onChangeStatus={changeStatus}
          onUpdateQuick={updateQuick}
          users={users}
          funcionarios={funcionarios}
        />
      )}

      {/* Modal de criação de Demanda */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Nova Demanda</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <input
                    type="text"
                    value={form.descr_dem}
                    onChange={(e) => setForm(f => ({ ...f, descr_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Responsável</label>
                  <select
                    value={form.respon_dem}
                    onChange={(e) => setForm(f => ({ ...f, respon_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecione</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Solicitante</label>
                  <select
                    value={form.solic_dem}
                    onChange={(e) => setForm(f => ({ ...f, solic_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Selecione</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                  <select
                    value={form.pri_dem}
                    onChange={(e) => setForm(f => ({ ...f, pri_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    {PRIORIDADES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Prevista</label>
                  <input
                    type="date"
                    value={form.dt_prev_dem}
                    onChange={(e) => setForm(f => ({ ...f, dt_prev_dem: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Reagendada</label>
                  <input
                    type="date"
                    value={form.dt_prev_dem_seg}
                    onChange={(e) => setForm(f => ({ ...f, dt_prev_dem_seg: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <IconButton
                  icon={X}
                  onClick={closeModal}
                  variant="secondary"
                  title="Cancelar"
                />
                <IconButton
                  icon={Save}
                  onClick={onCreate}
                  disabled={creating}
                  variant="primary"
                  title="Salvar"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Demandas;